package com.dynamicruntime.common.user

import com.dynamicruntime.common.gedra.clientLabel
import com.dynamicruntime.common.schema.SCT
import com.dynamicruntime.common.schema.SchTypesBuilder
import com.dynamicruntime.common.util.getOptLong
import com.dynamicruntime.common.util.getOptStr

/** Attribute keys of a [UserChoice]'s wire form ([UserChoice.toInfo]). Each name matches its value. */
@Suppress("ConstPropertyName")
object UCF {
    const val userId = "userId"
    const val client = "client"
    const val persona = "persona"
    const val personaSuffix = "personaSuffix"
    const val name = "name"
    const val isCurrent = "isCurrent"
    const val isDefault = "isDefault"
    const val signsInAs = "signsInAs"
    const val clientName = "clientName"
}

/**
 * One of the users a signed-in person may act as (issue #749): what the account menu lists and what
 * `switchUser` takes. Only a **registered, enabled** user is ever offered -- an unclaimed one an administrator
 * provisioned is not the person's to switch into until they prove it -- so neither state needs a field here.
 *
 * In the kernel so the frontend parses the same shape the backend serves and labels it by the same rule.
 */
data class UserChoice(
    val userId: Long,
    val client: String,
    val persona: String,
    /** The UAT batch discriminator (issue #747); empty for the ordinary user. */
    val personaSuffix: String = "",
    /** The user's real-world name, when it has one. */
    val name: String? = null,
    /** Whether this is the user the session is acting as. */
    val isCurrent: Boolean = false,
    /** Whether this is the identity's chosen default (`setDefaultUser`), as distinct from the fallback rule. */
    val isDefault: Boolean = false,
    /** The name of the user's [client] (issue #1110), what a person calls it; null when it has none to give. */
    val clientName: String? = null,
    /**
     * Whether a sign-in by the person's address lands on this user (issue #1330): the chosen default when it can be
     * used, else the one last acted as -- the rule worked out, where [isDefault] is only the choice. The two differ
     * when a chosen default is disabled, unclaimed, or in a client not carried here, and sign-in passes it over.
     */
    val signsInAs: Boolean = false,
) {
    /**
     * The full label: the client and persona, the personaSuffix when there is one, and the name when there is one --
     * `acme / Admin`, `acme / Member 2 -- Ada Lovelace`; the client as `Acme (acme)` when its name is known. Client first because it is what most often differs
     * between a person's users; the name last because it usually does not. What a tooltip says; the bar shows
     * the shorter `qualifierWithin`. Pure, covered under `jsNodeTest`.
     */
    fun label(): String = userKeyLabel(clientName?.let { clientLabel(client, it) } ?: client, persona, personaSuffix, name)

    /**
     * What tells this user from the others in [siblings], the person's users (issue #749): the client when
     * they span clients, the persona when they differ in persona **or any of them carries a personaSuffix** (so a
     * batch reads `Member`, `Member B` rather than a bare `B`), and the personaSuffix when there is one -- joined
     * with ` · `. Never empty for a list of two or more, since the key is unique across a person's users; empty
     * for a person with one user, who has nothing to tell apart. Given the user's [roles], a persona that is shown
     * says the access the user has when that differs from the persona's ([PERSONA.withActing], issue #1450): `Member
     * (Administrator access)`. Pure, covered under `jsNodeTest`.
     */
    fun qualifierWithin(siblings: List<UserChoice>, roles: Collection<String>? = null): String {
        if (siblings.size < 2) return ""
        val personaShown = siblings.any { it.persona != persona } || siblings.any { it.personaSuffix.isNotEmpty() }
        // The persona and the personaSuffix read as one term (`Member 2`), as they do in the full label.
        val personaPart = listOfNotNull(if (personaShown) PERSONA.label(persona) else null, personaSuffix.ifEmpty { null })
        val parts = buildList {
            // By its name when it has one (issue #1110): `Acme East`, not `acme_east`. The id is the tooltip's.
            if (siblings.any { it.client != client }) add(clientName?.ifBlank { null } ?: client)
            // A suffix of its own makes the persona shown, so a persona part is always the persona's.
            if (personaPart.isNotEmpty()) add(PERSONA.withActing(personaPart.joinToString(" "), persona, roles))
        }
        return parts.joinToString(" · ")
    }

    fun toInfo(): Map<String, Any?> = buildMap {
        put(UCF.userId, userId)
        put(UCF.client, client)
        put(UCF.persona, persona)
        if (personaSuffix.isNotEmpty()) put(UCF.personaSuffix, personaSuffix)
        if (name != null) put(UCF.name, name)
        if (isCurrent) put(UCF.isCurrent, true)
        if (isDefault) put(UCF.isDefault, true)
        if (clientName != null) put(UCF.clientName, clientName)
        if (signsInAs) put(UCF.signsInAs, true)
    }

    @Suppress("ConstPropertyName")
    companion object {
        /** Schema type name for [toInfo]. */
        const val infoTypeName = "UserChoice"

        /** The mirror of [toInfo]; a map missing its id is not a choice and yields null. */
        fun fromInfo(info: Map<String, Any?>): UserChoice? = UserChoice(
            userId = info.getOptLong(UCF.userId) ?: return null,
            client = info.getOptStr(UCF.client) ?: return null,
            persona = info.getOptStr(UCF.persona) ?: PERSONA.member,
            personaSuffix = info.getOptStr(UCF.personaSuffix) ?: "",
            name = info.getOptStr(UCF.name),
            isCurrent = info[UCF.isCurrent] == true,
            isDefault = info[UCF.isDefault] == true,
            clientName = info.getOptStr(UCF.clientName),
            signsInAs = info[UCF.signsInAs] == true,
        )

        /** Defines the [infoTypeName] schema type (the shape of [toInfo]) on [builder], kept beside the serialization. */
        fun defineInfoType(builder: SchTypesBuilder) {
            builder.type(infoTypeName) {
                type = SCT.kObject
                property(UCF.userId, "The user's numeric id.", required = true) { type = SCT.integer }
                property(UCF.client, "The client the user belongs to.", required = true)
                property(UCF.persona, "The user's persona.", required = true)
                property(UCF.personaSuffix, "The UAT batch discriminator; absent for the ordinary user.")
                property(UCF.name, "The user's real-world name, when it has one.")
                property(UCF.isCurrent, "Whether the session is acting as this user.") { type = SCT.boolean }
                property(UCF.isDefault, "Whether this is the identity's chosen default user.") { type = SCT.boolean }
                property(UCF.clientName, "The name of the user's client, when it has one.")
                property(UCF.signsInAs, "Whether a sign-in by the person's address lands on this user.") { type = SCT.boolean }
            }
        }
    }
}

/**
 * A user's persona and personaSuffix read as one term (issue #747): `Admin`, `Member B`, `Admin 2` -- the persona's
 * label, then the suffix when there is one. Pure, covered under `jsNodeTest`.
 */
fun personaTerm(persona: String, personaSuffix: String): String =
    if (personaSuffix.isEmpty()) PERSONA.label(persona) else "${PERSONA.label(persona)} $personaSuffix"

/**
 * The tag a listing puts beside a user to tell them apart (issue #1097): the [personaTerm], or null for a plain
 * member with no suffix -- the default and the common case, which a column tagged on every row would bury. So what
 * needs telling apart is what stands out: a person's admin user beside their member one, or `Member B` in a batch.
 * Pure, covered under `jsNodeTest`.
 */
fun personaTag(persona: String, personaSuffix: String): String? =
    if (persona == PERSONA.member && personaSuffix.isEmpty()) null else personaTerm(persona, personaSuffix)

/**
 * A user's full label: the client and [personaTerm], and the name when there is one -- `acme / Admin -- Ada
 * Lovelace`, `acme / Member 2`. What the switcher's tooltip says ([UserChoice.label]) and a listing's tag shows on
 * hover. Pure, covered under `jsNodeTest`.
 */
fun userKeyLabel(client: String, persona: String, personaSuffix: String, name: String?): String {
    val key = "$client / ${personaTerm(persona, personaSuffix)}"
    val shown = name?.trim()?.ifEmpty { null } ?: return key
    return "$key -- $shown"
}
